import styled from "@emotion/styled"
import { PButtonPure, PHeading, PLinkPure } from "@porsche-design-system/components-react"
import { spacingStaticMd, colorContrastLower } from "@porsche-design-system/components-react/emotion"
import type { NotificationListItem } from "@slfinrtl/ui-bff-routes"
import type { MouseEvent } from "react"
import { useState } from "react"
import { FormattedMessage } from "react-intl"
import { Link } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { commonMessages } from "src/common/messages/common.messages"
import { Flex } from "src/components"
import { NotificationList } from "../notificationList/NotificationList"
import { NotificationListSkeleton } from "../notificationList/NotificationListSkeleton"
import { notificationPopoverContentMessages as messages } from "./notificationPopoverContent.messages"

const INITIAL_VISIBLE_COUNT = 9

export type NotificationLink = "open_notification" | "open_notification_center" | "open_notification_settings"
interface NotificationPopoverContentProps {
	className?: string
	isPending: (id: string) => boolean
	items?: NotificationListItem[]
	updateNotificationStatus: (id: string, newStatus: "READ" | "SEEN") => void
	url?: string

	onLinkClick?: (action: NotificationLink, e: MouseEvent<HTMLElement>) => void
}

export const NotificationPopoverContent = ({
	className,
	isPending,
	items,
	onLinkClick,
	updateNotificationStatus,
	url,
}: NotificationPopoverContentProps) => {
	const routes = useDealerAppRoutes()
	const [isExpanded, setIsExpanded] = useState(false)

	const isExpandable = (items?.length ?? 0) > INITIAL_VISIBLE_COUNT
	const hiddenCount = isExpandable ? Math.max(0, (items?.length ?? 0) - INITIAL_VISIBLE_COUNT) : 0

	const onNotificationClick = (e: MouseEvent<HTMLElement>) => onLinkClick?.("open_notification", e)
	const onNotificationCenterClick = (e: MouseEvent<HTMLElement>) => onLinkClick?.("open_notification_center", e)
	const onNotificationSettingsClick = (e: MouseEvent<HTMLElement>) => onLinkClick?.("open_notification_settings", e)
	const onToggleExpand = () => setIsExpanded((previous) => !previous)

	return (
		<Layout className={className}>
			<Flex $justifyContent="space-between">
				<PHeading tag="h4" size="sm" weight="semibold">
					<FormattedMessage {...messages.heading} />
				</PHeading>
				{routes?.settings?.notificationSettings && (
					<PLinkPure onClick={onNotificationSettingsClick} hideLabel icon="adjust" size="sm">
						<Link to={routes.settings.notificationSettings.url()} />
					</PLinkPure>
				)}
			</Flex>

			<NotificationListWrapper>
				{items === undefined ? (
					<NotificationListSkeleton compact />
				) : (
					<NotificationList
						items={isExpandable && !isExpanded ? items.slice(0, INITIAL_VISIBLE_COUNT) : items}
						isPending={isPending}
						updateNotificationStatus={updateNotificationStatus}
						compact
						onNotificationClick={onNotificationClick}
					/>
				)}
			</NotificationListWrapper>

			{hiddenCount > 0 && (
				<ShowMoreButton
					icon={isExpanded ? "arrow-head-up" : "arrow-head-down"}
					onClick={onToggleExpand}
					alignLabel="start"
				>
					{isExpanded ? (
						<FormattedMessage {...commonMessages.showLess} />
					) : (
						<FormattedMessage {...messages.expandableShowMore} values={{ count: hiddenCount }} />
					)}
				</ShowMoreButton>
			)}

			{url && (
				<StyledLinkPure alignLabel="start" onClick={onNotificationCenterClick}>
					<Link to={url}>
						<FormattedMessage {...messages.link} />
					</Link>
				</StyledLinkPure>
			)}
		</Layout>
	)
}

const Layout = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticMd};
	max-width: 100%;
`

const NotificationListWrapper = styled.div`
	border-top: 1px solid ${colorContrastLower};
	border-bottom: 1px solid ${colorContrastLower};
	/* To prevent the popover from positioning itself to the left of the trigger button */
	max-height: calc(100vh - 285px);
	overflow-x: hidden;
	overflow-y: auto;
`

const StyledLinkPure = styled(PLinkPure)`
	align-self: end;
`

const ShowMoreButton = styled(PButtonPure)`
	align-self: start;
`
